/*
 * Bootstrapper — wires the vendored habitat module to the theme.
 *
 * Discovers per-component `habitat.js` (sync) and `habitat-async.js` (async
 * loader) files via webpack's require.context, wraps each with the Redux
 * Provider HOC, registers them with the vendored habitat module, and calls
 * populate() to mount everything currently in the DOM (plus install the
 * MutationObservers for dynamic mounts).
 *
 * Exports a singleton with `getStore()` — preserved for compatibility with
 * `higher-order-components/withReduxStore.js` and Gutenberg blocks that call
 * `app.getStore()` directly.
 *
 * NOTE: downstream forks' existing habitat.js / habitat-async.js files do
 * NOT need to be edited when picking up this change — the HTML contract
 * (data-component, data-prop-*, etc.) and the per-component export shapes
 * are preserved exactly.
 */
import chain from 'ramda/es/chain'
import toPairs from 'ramda/es/toPairs'

import createStore from '../../redux/create'
import { getDuplicateKeysString, objectsShareKey } from '../../util/state'

import { register, unRegisterAll, disposeAll, populate } from './habitat'
import { withStore, withStoreAsync } from './withStore'

const syncContextLoaders = () => [
  require.context('../../components/', true, /habitat\.js$/),
  require.context('../../../vc_elements/', true, /habitat\.js$/),
]

const asyncContextLoaders = () => [
  require.context('../../components/', true, /habitat-async\.js$/),
  require.context('../../../vc_elements/', true, /habitat-async\.js$/),
]

/*
 * Given webpack require.contexts, collect the default export of each matched
 * file into a list of objects. Each object is `{ ComponentName: value }`
 * where `value` is either a React component (sync) or a loader function
 * returning a Promise (async).
 */
const contextsToExports = contexts =>
  chain(context => context.keys().map(key => context(key).default), contexts)

const warnOnDuplicates = allExports => {
  if (process.env.NODE_ENV === 'production') return
  if (!objectsShareKey(allExports)) return
  const names = getDuplicateKeysString(allExports)
  // eslint-disable-next-line no-console
  console.error(
    `habitat: duplicate component names registered: ${names}. ` +
      'Check your habitat.js and habitat-async.js files.'
  )
}

const registerAll = store => {
  const syncExports = contextsToExports(syncContextLoaders())
  const asyncExports = contextsToExports(asyncContextLoaders())

  warnOnDuplicates([...syncExports, ...asyncExports])

  for (const [name, Component] of chain(toPairs, syncExports)) {
    register(name, withStore(store, Component))
  }
  for (const [name, loader] of chain(toPairs, asyncExports)) {
    register(name, withStoreAsync(store, loader), { async: true })
  }
}

const store = createStore()

registerAll(store)
populate()

if (module.hot) {
  // Re-register and re-populate when any habitat.js / habitat-async.js changes.
  // Uses the full path glob as the HMR dependency list.
  const moduleIds = [...syncContextLoaders(), ...asyncContextLoaders()].map(
    c => c.id
  )
  module.hot.accept(moduleIds, () => {
    disposeAll()
    unRegisterAll()
    registerAll(store)
    populate()
  })
}

/*
 * The public singleton. `getStore()` is the primary surface (used by
 * withReduxStore.js and Gutenberg blocks). `update` and `dispose` are
 * no-op shims kept for compatibility with downstream forks that may call
 * `app.update()` / `app.dispose()` in custom code (e.g. HMR hooks, dev
 * console). These were part of react-habitat's imperative API and are
 * redundant here: the vendored habitat module's MutationObserver handles
 * dynamic mounts automatically, and internal HMR uses `disposeAll`
 * imported from `./habitat` directly.
 */
const warnNoOp = method => () => {
  if (process.env.NODE_ENV !== 'production') {
    // eslint-disable-next-line no-console
    console.warn(
      `app.${method}() is a no-op — the vendored habitat module handles ` +
        'mounts/unmounts automatically via MutationObserver. Remove the call ' +
        '(or use disposeAll() from scripts/init/react/habitat for full teardown).'
    )
  }
}

const app = {
  getStore: () => store,
  update: warnNoOp('update'),
  dispose: warnNoOp('dispose'),
}

export default app
